// src/pages/AskOSAdmin.js
// AskOS Student Management — Tenant Admin tool
// Login pattern mirrors ClassroomLoginPage exactly:
//   - Slug as first form field with branding preflight on change
//   - Slug pre-filled (but still editable) if arriving via /tenant/:slug/askos-admin

import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Row, Col, Card, Table, Form, Button,
    Alert, Badge, Spinner, Modal, Nav, InputGroup
} from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Users, BookOpen, Link2, Plus, Trash2, KeyRound,
    Pencil, Search, GraduationCap, LogOut, ShieldCheck,
    ToggleLeft, ToggleRight, CheckCircle2, XCircle,
    Unlink, ExternalLink, Zap, BarChart2, Upload, Newspaper
} from 'lucide-react';
import api from '../api/api';
import { logout } from '../redux/slices/userSlice';
import '../components/Projects/Copilot.css';

const adminAuthHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
});

// ── Shared UI helpers ─────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => (
    <Badge
        className={status === 'active' ? 'badge-soft-success' : 'bg-secondary bg-opacity-10 text-secondary'}
        style={{ fontSize: '0.7rem' }}
    >
        {status === 'active'
            ? <CheckCircle2 size={10} className="me-1" />
            : <XCircle size={10} className="me-1" />
        }
        {status}
    </Badge>
);

const EmptyRow = ({ cols, icon: Icon, message }) => (
    <tr>
        <td colSpan={cols} className="text-center py-5 text-muted">
            <Icon size={28} className="mb-2 opacity-50 d-block mx-auto" />
            <span className="small">{message}</span>
        </td>
    </tr>
);

const useAlert = () => {
    const [alert, setAlert] = useState(null);
    const showAlert = (msg, variant = 'success') => {
        setAlert({ msg, variant });
        setTimeout(() => setAlert(null), 3500);
    };
    return [alert, showAlert];
};


// ═══════════════════════════════════════════════════════════════════════════ //
//  ADMIN LOGIN                                                                //
// ═══════════════════════════════════════════════════════════════════════════ //
const AdminLogin = ({ onLogin }) => {
    // Pre-fill slug from URL if arriving via /tenant/:slug/askos-admin
    const { slug: urlSlug } = useParams();

    const [slug, setSlug]         = useState(urlSlug || '');
    const [email, setEmail]       = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');
    const [branding, setBranding] = useState({ institutionName: '', logoUrl: '' });

    // Branding preflight — identical to ClassroomLoginPage
    useEffect(() => {
        if (slug.length >= 3) {
            api.get(`/public/tenant-branding/${slug}`)
                .then(res => setBranding({
                    institutionName: res.data.name || '',
                    logoUrl:         res.data.logo  || '',
                }))
                .catch(() => setBranding({ institutionName: '', logoUrl: '' }));
        } else {
            setBranding({ institutionName: '', logoUrl: '' });
        }
    }, [slug]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true); setError('');
        try {
            // Admin logs in via the standard tenant token endpoint
            const res = await api.post(
                `/tenant/${slug}/token`,
                new URLSearchParams({ username: email, password })
            );
            localStorage.setItem('admin_token', res.data.access_token);
            localStorage.setItem('admin_slug',  slug);
            onLogin(slug);
        } catch (err) {
            setError(
                err.response?.data?.detail ||
                'Verification failed. Ensure your institutional slug and credentials are correct.'
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container fluid className="vh-100 d-flex align-items-center justify-content-center bg-light">
            <Row
                className="w-100 shadow-lg rounded-4 overflow-hidden"
                style={{ maxWidth: '1000px', minHeight: '600px' }}
            >
                {/* Left branding panel */}
                <Col md={6} className="bg-primary d-none d-md-flex flex-column justify-content-center p-5 text-white">
                    <div className="mb-4 text-center">
                        {branding.logoUrl ? (
                            <img src={branding.logoUrl} alt={branding.institutionName} height="80" className="mb-3" />
                        ) : (
                            <img src="/assets/images/logo.png" alt="AskOS Admin" height="60" className="mb-3" />
                        )}
                        <h2 className="fw-bold">
                            {branding.institutionName ? `${branding.institutionName} — Admin` : 'AskOS Admin'}
                        </h2>
                        <p className="opacity-75">Student management for your institution.</p>
                    </div>
                    <div className="mt-5">
                        <div className="d-flex align-items-start gap-3 mb-4">
                            <div className="bg-white bg-opacity-25 p-2 rounded"><Users size={20} /></div>
                            <div>
                                <h6 className="mb-1">Student Management</h6>
                                <small className="opacity-75">Add, edit, reset, and manage student accounts.</small>
                            </div>
                        </div>
                        <div className="d-flex align-items-start gap-3">
                            <div className="bg-white bg-opacity-25 p-2 rounded"><Link2 size={20} /></div>
                            <div>
                                <h6 className="mb-1">Course–Project Mapping</h6>
                                <small className="opacity-75">Map LMS course IDs to your Academicos projects.</small>
                            </div>
                        </div>
                    </div>
                </Col>

                {/* Right login form */}
                <Col md={6} className="p-5 d-flex flex-column justify-content-center bg-white">
                    <div className="mb-4 text-center text-md-start">
                        <h3 className="fw-bold text-dark">Admin Sign In</h3>
                        <p className="text-muted small">Manage AskOS students for your institution</p>
                    </div>

                    {error && <Alert variant="danger" className="py-2 small" role="alert">{error}</Alert>}

                    <Form onSubmit={handleSubmit}>
                        {/* University Slug — same pattern as ClassroomLoginPage */}
                        <Form.Group className="mb-3">
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">
                                University Slug
                            </Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g. tau-gy"
                                value={slug}
                                onChange={e => setSlug(e.target.value.toLowerCase().trim())}
                                required
                                className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">
                                Admin Email
                            </Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="admin@university.edu"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                                className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">
                                Password
                            </Form.Label>
                            <Form.Control
                                type="password"
                                placeholder="••••••••"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                required
                                className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent"
                            />
                        </Form.Group>

                        <Button
                            variant="primary"
                            type="submit"
                            className="w-100 fw-bold py-3 shadow mb-3"
                            disabled={loading}
                        >
                            {loading
                                ? <Spinner animation="border" size="sm" aria-hidden="true" />
                                : 'Enter Admin Panel'
                            }
                        </Button>
                    </Form>

                    <div className="mt-4 text-center">
                        <Badge bg="light" text="dark" className="border px-3 py-2 fw-normal rounded-pill">
                            <ShieldCheck size={14} className="me-2 text-primary" />
                            {slug
                                ? `Tenant Administrator · ${slug.toUpperCase()}`
                                : 'Institutional Administrator'
                            }
                        </Badge>
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

// Shared underline input
    const UInput = ({ label, ...props }) => (
        <Form.Group className="mb-3">
            <Form.Label className="x-small fw-bold text-muted text-uppercase">{label}</Form.Label>
            <Form.Control className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent" {...props} />
        </Form.Group>
    );

// ═══════════════════════════════════════════════════════════════════════════ //
//  STUDENTS TAB                                                               //
// ═══════════════════════════════════════════════════════════════════════════ //
const StudentsTab = ({ slug }) => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [search, setSearch]     = useState('');
    const [modal, setModal]       = useState(null);
    const [selected, setSelected] = useState(null);
    const [form, setForm]         = useState({});
    const [saving, setSaving]     = useState(false);
    const [alert, showAlert]      = useAlert();

    const LANGS = [
        { value: 'en', label: 'English' },
        { value: 'ta', label: 'Tamil'   },
        { value: 'hi', label: 'Hindi'   },
        { value: 'te', label: 'Telugu'  },
        { value: 'ml', label: 'Malayalam' },
        { value: 'kn', label: 'Kannada' },
    ];
    const VOICES = [
        { value: 'nova',    label: 'Nova (Female, warm)'   },
        { value: 'shimmer', label: 'Shimmer (Female, clear)' },
        { value: 'alloy',   label: 'Alloy (Neutral)'       },
        { value: 'echo',    label: 'Echo (Male)'           },
    ];

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/tenant/${slug}/askos/admin/students`, { headers: adminAuthHeader() });
            setStudents(res.data);
        } catch { setStudents([]); }
        finally { setLoading(false); }
    }, [slug]);

    useEffect(() => { load(); }, [load]);

    const filtered = students.filter(s =>
        `${s.email} ${s.full_name || ''}`.toLowerCase().includes(search.toLowerCase())
    );

    const save = async () => {
        setSaving(true);
        try {
            if (modal === 'add') {
                await api.post(`/tenant/${slug}/askos/register`, form, { headers: adminAuthHeader() });
                showAlert('Student account created successfully.');
           } else if (modal === 'edit') {
                // 🌟 FIX: Explicitly capture all modal fields, ensuring nulls for unique constraints
                const payload = {
                    full_name: form.full_name || null,
                    language_preference: form.language_preference || 'en',
                    voice_preference: form.voice_preference || 'nova',
                    telegram_id: form.telegram_id || null,
                    telegram_username: form.telegram_username || null,
                    phone_number: form.phone_number || null,
                };
                
                await api.patch(`/tenant/${slug}/askos/admin/students/${selected.id}`, payload, { headers: adminAuthHeader() });
                showAlert('Student profile updated.');
            } else if (modal === 'reset') {
                await api.patch(`/tenant/${slug}/askos/admin/students/${selected.id}/reset-password`, { password: form.password }, { headers: adminAuthHeader() });
                showAlert('Password has been reset.');
            } else if (modal === 'delete') {
                await api.delete(`/tenant/${slug}/askos/admin/students/${selected.id}`, { headers: adminAuthHeader() });
                showAlert('Student deactivated.', 'warning');
            }
            setModal(null);
            load();
        } catch (e) {
            showAlert(e.response?.data?.detail || 'Action failed. Please try again.', 'danger');
        } finally {
            setSaving(false);
        }
    };

    // Shared modal footer
    const ModalFooter = ({ confirmLabel, confirmVariant = 'primary' }) => (
        <Modal.Footer className="border-0 pt-0">
            <Button variant="light" onClick={() => setModal(null)}>Cancel</Button>
            <Button variant={confirmVariant} onClick={save} disabled={saving} className="rounded-pill px-4">
                {saving ? <Spinner animation="border" size="sm" aria-hidden="true" /> : confirmLabel}
            </Button>
        </Modal.Footer>
    );

    

    return (
        <>
            {alert && <Alert variant={alert.variant} className="py-2 small mb-3" role="status">{alert.msg}</Alert>}

            <div className="d-flex align-items-center justify-content-between mb-3">
                <InputGroup style={{ maxWidth: 300 }}>
                    <InputGroup.Text className="bg-transparent border-end-0">
                        <Search size={14} className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control placeholder="Search students…" value={search}
                        onChange={e => setSearch(e.target.value)} className="border-start-0 shadow-none" />
                </InputGroup>
                <Button variant="primary" size="sm" className="rounded-pill d-flex align-items-center gap-2"
                    onClick={() => { setForm({ language_preference: 'en' }); setModal('add'); }}>
                    <Plus size={14} /> Add Student
                </Button>
            </div>

            <Card className="faculty-card">
                <Table responsive hover className="mb-0 small">
                    <thead className="table-light">
                        <tr>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3 px-4">Student</th>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Status</th>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Language</th>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Last Login</th>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3 text-end pe-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5} className="text-center py-4"><Spinner animation="border" size="sm" aria-hidden="true" /></td></tr>
                        ) : filtered.length === 0 ? (
                            <EmptyRow cols={5} icon={Users} message="No students found. Add your first student above." />
                        ) : filtered.map(s => (
                            <tr key={s.id}>
                                <td className="py-3 px-4">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center fw-bold text-primary"
                                            style={{ width: 34, height: 34, fontSize: '0.8rem', flexShrink: 0 }}>
                                            {(s.full_name || s.email || '?')[0].toUpperCase()}
                                        </div>
                                        <div>
                                            <div className="fw-semibold text-dark">{s.full_name || '—'}</div>
                                            <div className="x-small text-muted d-flex align-items-center gap-2 flex-wrap">
                                                <span>{s.email}</span>
                                                {s.telegram_id && (
                                                    <span title={`Telegram: ${s.telegram_id}${s.telegram_username ? ' @' + s.telegram_username : ''}`}
                                                        style={{ color: '#229ED9', fontWeight: 700, fontSize: '0.62rem' }}>
                                                        ✈ TG
                                                    </span>
                                                )}
                                                {s.phone_number && (
                                                    <span title={`WhatsApp: ${s.phone_number}`}
                                                        style={{ color: '#25D366', fontWeight: 700, fontSize: '0.62rem' }}>
                                                        📱 WA
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td className="py-3 align-middle"><StatusBadge status={s.status} /></td>
                                <td className="py-3 align-middle text-muted">{s.language_preference || 'en'}</td>
                                <td className="py-3 align-middle text-muted x-small">
                                    {s.last_login ? new Date(s.last_login).toLocaleDateString() : 'Never'}
                                </td>
                                <td className="py-3 pe-4 align-middle text-end">
                                    <div className="d-flex align-items-center justify-content-end gap-2">
                                        <Button variant="outline-secondary" size="sm" className="d-flex align-items-center gap-1 rounded-pill"
                                            onClick={() => { setSelected(s); setForm({ full_name: s.full_name, language_preference: s.language_preference, voice_preference: s.voice_preference, telegram_id: s.telegram_id || '', telegram_username: s.telegram_username || '', phone_number: s.phone_number || '' }); setModal('edit'); }}>
                                            <Pencil size={12} /> Edit
                                        </Button>
                                        <Button variant="outline-warning" size="sm" className="d-flex align-items-center gap-1 rounded-pill"
                                            onClick={() => { setSelected(s); setForm({ password: '' }); setModal('reset'); }}>
                                            <KeyRound size={12} /> Reset PW
                                        </Button>
                                        <Button variant="outline-danger" size="sm" className="d-flex align-items-center gap-1 rounded-pill"
                                            onClick={() => { setSelected(s); setModal('delete'); }}>
                                            <Trash2 size={12} /> Remove
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Card>

            {/* Add Modal */}
            <Modal show={modal === 'add'} onHide={() => setModal(null)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-6">Add New Student</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <UInput label="Full Name" placeholder="Jane Doe" value={form.full_name || ''} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
                    <UInput label="University Email" type="email" placeholder="jane@university.edu" value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                    <UInput label="Temporary Password" type="password" placeholder="••••••••" value={form.password || ''} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Preferred Language</Form.Label>
                        <Form.Select value={form.language_preference || 'en'} onChange={e => setForm(f => ({ ...f, language_preference: e.target.value }))} className="shadow-none">
                            {LANGS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                        </Form.Select>
                    </Form.Group>
                    <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />
                    <p className="x-small fw-bold text-muted text-uppercase mb-1" style={{ letterSpacing: '0.07em' }}>
                        📱 Messaging Platforms <span className="fw-normal text-muted">(optional)</span>
                    </p>
                    <p className="x-small text-muted mb-3">
                        Students can also self-link Telegram by sending /link to the bot.
                    </p>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Telegram ID (chat_id)</Form.Label>
                        <Form.Control className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent"
                            placeholder="e.g. 987654321"
                            value={form.telegram_id || ''}
                            onChange={e => setForm(f => ({ ...f, telegram_id: e.target.value }))} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Telegram Username</Form.Label>
                        <Form.Control className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent"
                            placeholder="studenthandle (without @)"
                            value={form.telegram_username || ''}
                            onChange={e => setForm(f => ({ ...f, telegram_username: e.target.value }))} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Phone Number (WhatsApp)</Form.Label>
                        <Form.Control className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent"
                            placeholder="+919876543210 (E.164 format)"
                            value={form.phone_number || ''}
                            onChange={e => setForm(f => ({ ...f, phone_number: e.target.value }))} />
                    </Form.Group>
                </Modal.Body>
                <ModalFooter confirmLabel="Add Student" />
            </Modal>

            {/* Edit Modal */}
            <Modal show={modal === 'edit'} onHide={() => setModal(null)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-6">Edit — {selected?.email}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <UInput label="Full Name" value={form.full_name || ''} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Preferred Language</Form.Label>
                        <Form.Select value={form.language_preference || 'en'} onChange={e => setForm(f => ({ ...f, language_preference: e.target.value }))} className="shadow-none">
                            {LANGS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Voice Preference</Form.Label>
                        <Form.Select value={form.voice_preference || 'nova'} onChange={e => setForm(f => ({ ...f, voice_preference: e.target.value }))} className="shadow-none">
                            {VOICES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                        </Form.Select>
                    </Form.Group>
                    <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />
                    <p className="x-small fw-bold text-muted text-uppercase mb-1" style={{ letterSpacing: '0.07em' }}>
                        📱 Messaging Platforms
                    </p>
                    {/* Telegram link status */}
                    {selected?.telegram_id && !form.telegram_id && (
                        <div className="d-flex align-items-center justify-content-between p-2 rounded-3 mb-3"
                            style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                            <div>
                                <p className="x-small fw-semibold mb-0" style={{ color: '#15803d' }}>✈ Telegram Linked</p>
                                <p className="x-small text-muted mb-0">
                                    ID: {selected.telegram_id}
                                    {selected.telegram_username && ` · @${selected.telegram_username}`}
                                </p>
                            </div>
                            <Button size="sm" variant="outline-danger" className="rounded-pill d-flex align-items-center gap-1"
                                onClick={() => setForm(f => ({ ...f, telegram_id: '', telegram_username: '' }))}
                                title="Clear Telegram link — student will need to re-link">
                                <Unlink size={12} />Unlink
                            </Button>
                        </div>
                    )}
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Telegram ID (chat_id)</Form.Label>
                        <Form.Control className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent"
                            placeholder="e.g. 987654321 — or clear to unlink"
                            value={form.telegram_id || ''}
                            onChange={e => setForm(f => ({ ...f, telegram_id: e.target.value }))} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Telegram Username</Form.Label>
                        <Form.Control className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent"
                            placeholder="studenthandle (without @)"
                            value={form.telegram_username || ''}
                            onChange={e => setForm(f => ({ ...f, telegram_username: e.target.value }))} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Phone Number (WhatsApp)</Form.Label>
                        <Form.Control className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent"
                            placeholder="+919876543210 (E.164 format)"
                            value={form.phone_number || ''}
                            onChange={e => setForm(f => ({ ...f, phone_number: e.target.value }))} />
                    </Form.Group>
                </Modal.Body>
                <ModalFooter confirmLabel="Save Changes" />
            </Modal>

            {/* Reset PW Modal */}
            <Modal show={modal === 'reset'} onHide={() => setModal(null)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-6">Reset Password — {selected?.email}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <UInput label="New Password" type="password" placeholder="New temporary password" value={form.password || ''} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
                </Modal.Body>
                <ModalFooter confirmLabel="Reset Password" confirmVariant="warning" />
            </Modal>

            {/* Delete Modal */}
            <Modal show={modal === 'delete'} onHide={() => setModal(null)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-6 text-danger">Remove Student?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="small text-muted">
                        This will deactivate <strong>{selected?.email}</strong>. They will immediately lose access to AskOS. This can be reversed by editing the student's status.
                    </p>
                </Modal.Body>
                <ModalFooter confirmLabel="Remove Student" confirmVariant="danger" />
            </Modal>
        </>
    );
};


// ═══════════════════════════════════════════════════════════════════════════ //
//  PROJECT ACCESS TAB                                                         //
// ═══════════════════════════════════════════════════════════════════════════ //
const ProjectAccessTab = ({ slug }) => {
    const [students, setStudents] = useState([]);
    const [projects, setProjects] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [selected, setSelected] = useState(null);
    const [alert, showAlert]      = useAlert();

    useEffect(() => {
        const load = async () => {
            try {
                const [s, p, m] = await Promise.all([
                    api.get(`/tenant/${slug}/askos/admin/students`,                { headers: adminAuthHeader() }),
                    api.get(`/tenant/${slug}/askos/admin/projects`,                { headers: adminAuthHeader() }),
                    api.get(`/tenant/${slug}/askos/admin/student-project-mappings`,{ headers: adminAuthHeader() }),
                ]);
                setStudents(s.data); setProjects(p.data); setMappings(m.data);
            } catch { /* empty */ }
            setLoading(false);
        };
        load();
    }, [slug]);

    const hasAccess = (studentId, projectId) =>
        mappings.some(m => m.student_id === studentId && m.project_id === projectId);

    const toggle = async (studentId, projectId, currently) => {
        try {
            if (currently) {
                await api.delete(`/tenant/${slug}/askos/admin/student-project-mappings`,
                    { headers: adminAuthHeader(), data: { student_id: studentId, project_id: projectId } });
                setMappings(prev => prev.filter(m => !(m.student_id === studentId && m.project_id === projectId)));
                showAlert('Access removed.', 'warning');
            } else {
                await api.post(`/tenant/${slug}/askos/admin/student-project-mappings`,
                    { student_id: studentId, project_id: projectId }, { headers: adminAuthHeader() });
                setMappings(prev => [...prev, { student_id: studentId, project_id: projectId }]);
                showAlert('Access granted.');
            }
        } catch (e) { showAlert(e.response?.data?.detail || 'Action failed.', 'danger'); }
    };

    if (loading) return <div className="text-center py-5"><Spinner animation="border" size="sm" aria-hidden="true" /></div>;

    return (
        <>
            {alert && <Alert variant={alert.variant} className="py-2 small mb-3" role="status">{alert.msg}</Alert>}
            <p className="text-muted small mb-4">
                Select a student to manage which subject projects they can access in AskOS. Toggles take effect immediately.
            </p>
            <Row className="g-3">
                <Col md={4}>
                    <Card className="faculty-card h-100">
                        <Card.Header className="bg-transparent border-0 pb-0 pt-3 px-3">
                            <p className="x-small fw-bold text-muted text-uppercase mb-0">Students</p>
                        </Card.Header>
                        <Card.Body className="p-2">
                            {students.length === 0
                                ? <p className="x-small text-muted text-center py-4">No students added yet</p>
                                : students.map(s => (
                                    <button key={s.id}
                                        className={`d-flex align-items-center gap-3 w-100 border-0 rounded-3 px-3 py-2 mb-1 text-start transition ${selected?.id === s.id ? 'bg-primary text-white' : 'bg-transparent text-dark'}`}
                                        style={{ cursor: 'pointer' }} onClick={() => setSelected(s)}>
                                        <div className={`rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0 ${selected?.id === s.id ? 'bg-white text-primary' : 'bg-primary bg-opacity-10 text-primary'}`}
                                            style={{ width: 32, height: 32, fontSize: '0.75rem' }}>
                                            {(s.full_name || s.email)[0].toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="small fw-semibold text-truncate">{s.full_name || s.email}</div>
                                            <div className={`x-small text-truncate ${selected?.id === s.id ? 'opacity-75' : 'text-muted'}`}>
                                                {mappings.filter(m => m.student_id === s.id).length} subjects
                                            </div>
                                        </div>
                                    </button>
                                ))
                            }
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={8}>
                    <Card className="faculty-card h-100">
                        <Card.Header className="bg-transparent border-0 pb-0 pt-3 px-4">
                            <p className="x-small fw-bold text-muted text-uppercase mb-0">
                                {selected ? `Project access — ${selected.full_name || selected.email}` : 'Select a student'}
                            </p>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {!selected ? (
                                <div className="text-center py-5 text-muted"><Users size={28} className="mb-2 opacity-50" /><p className="small">Choose a student from the left</p></div>
                            ) : projects.length === 0 ? (
                                <div className="text-center py-5 text-muted"><BookOpen size={28} className="mb-2 opacity-50" /><p className="small">No approved projects available yet</p></div>
                            ) : (
                                <Table hover className="mb-0 small">
                                    <tbody>
                                        {projects.map(p => {
                                            const active = hasAccess(selected.id, p.id);
                                            return (
                                                <tr key={p.id}>
                                                    <td className="py-3 px-4">
                                                        <div className="fw-semibold text-dark">{p.name}</div>
                                                        <div className="x-small text-muted">Project #{p.id}</div>
                                                    </td>
                                                    <td className="py-3 px-4 text-end align-middle">
                                                        <Button variant={active ? 'primary' : 'outline-secondary'} size="sm"
                                                            className="rounded-pill d-inline-flex align-items-center gap-2"
                                                            onClick={() => toggle(selected.id, p.id, active)}>
                                                            {active ? <><ToggleRight size={14} /> Access On</> : <><ToggleLeft size={14} /> Access Off</>}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );
};


// ═══════════════════════════════════════════════════════════════════════════ //
//  COURSE MAPPING TAB                                                         //
// ═══════════════════════════════════════════════════════════════════════════ //
const CourseMappingTab = ({ slug }) => {
    const [mappings, setMappings] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [saving, setSaving]     = useState(false);
    const [alert, showAlert]      = useAlert();
    const [form, setForm]         = useState({ lms_provider: 'moodle', lms_course_id: '', lms_course_name: '', project_id: '' });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [m, p] = await Promise.all([
                api.get(`/tenant/${slug}/askos/admin/lms-mappings`, { headers: adminAuthHeader() }),
                api.get(`/tenant/${slug}/askos/admin/projects`,     { headers: adminAuthHeader() }),
            ]);
            setMappings(m.data); setProjects(p.data);
        } catch { /* empty */ }
        setLoading(false);
    }, [slug]);

    useEffect(() => { load(); }, [load]);

    const add = async () => {
        if (!form.lms_course_id || !form.project_id) return showAlert('Course ID and Project are required.', 'danger');
        setSaving(true);
        try {
            await api.post(`/tenant/${slug}/askos/admin/lms-mappings`,
                { ...form, project_id: parseInt(form.project_id) }, { headers: adminAuthHeader() });
            showAlert('Course mapping created.');
            setForm(f => ({ ...f, lms_course_id: '', lms_course_name: '' }));
            load();
        } catch (e) { showAlert(e.response?.data?.detail || 'Failed to create mapping.', 'danger'); }
        setSaving(false);
    };

    const remove = async (id) => {
        try {
            await api.delete(`/tenant/${slug}/askos/admin/lms-mappings/${id}`, { headers: adminAuthHeader() });
            showAlert('Mapping removed.', 'warning');
            load();
        } catch (e) { showAlert(e.response?.data?.detail || 'Failed to remove.', 'danger'); }
    };

    return (
        <>
            {alert && <Alert variant={alert.variant} className="py-2 small mb-3" role="status">{alert.msg}</Alert>}
            <p className="text-muted small mb-4">
                Map your LMS course IDs to Academicos projects. Once SSO is live, enrolled students will automatically get AskOS access.
            </p>

            <Card className="faculty-card mb-4">
                <Card.Body className="p-4">
                    <p className="x-small fw-bold text-muted text-uppercase mb-3">Add New Mapping</p>
                    <Row className="g-3 align-items-end">
                        <Col md={3}>
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">LMS Provider</Form.Label>
                            <Form.Select value={form.lms_provider} onChange={e => setForm(f => ({ ...f, lms_provider: e.target.value }))} className="shadow-none">
                                {['moodle','canvas','blackboard','sap','custom'].map(p => (
                                    <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col md={3}>
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">Course ID <span className="text-danger">*</span></Form.Label>
                            <Form.Control placeholder="e.g. CS301-2026-S1" value={form.lms_course_id} onChange={e => setForm(f => ({ ...f, lms_course_id: e.target.value }))} className="shadow-none" />
                        </Col>
                        <Col md={3}>
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">Course Name</Form.Label>
                            <Form.Control placeholder="Data Structures" value={form.lms_course_name} onChange={e => setForm(f => ({ ...f, lms_course_name: e.target.value }))} className="shadow-none" />
                        </Col>
                        <Col md={2}>
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">Project <span className="text-danger">*</span></Form.Label>
                            <Form.Select value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))} className="shadow-none">
                                <option value="">Select…</option>
                                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </Form.Select>
                        </Col>
                        <Col md={1} className="text-end">
                            <Button variant="primary" className="w-100 d-flex align-items-center justify-content-center gap-1 rounded-pill" onClick={add} disabled={saving}>
                                {saving ? <Spinner animation="border" size="sm" aria-hidden="true" /> : <><Plus size={14} /> Add</>}
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="faculty-card">
                <Table responsive hover className="mb-0 small">
                    <thead className="table-light">
                        <tr>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3 px-4">Provider</th>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Course ID</th>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Course Name</th>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Maps to Project</th>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Status</th>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3 pe-4 text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="text-center py-4"><Spinner animation="border" size="sm" aria-hidden="true" /></td></tr>
                        ) : mappings.length === 0 ? (
                            <EmptyRow cols={6} icon={Link2} message="No mappings yet. Add your first course mapping above." />
                        ) : mappings.map(m => {
                            const proj = projects.find(p => p.id === m.project_id);
                            return (
                                <tr key={m.id}>
                                    <td className="py-3 px-4"><Badge className="badge-soft-primary text-uppercase x-small">{m.lms_provider || 'custom'}</Badge></td>
                                    <td className="py-3 align-middle"><code className="x-small bg-light px-2 py-1 rounded">{m.lms_course_id}</code></td>
                                    <td className="py-3 align-middle text-muted">{m.lms_course_name || '—'}</td>
                                    <td className="py-3 align-middle"><span className="text-primary fw-semibold">{proj?.name || `#${m.project_id}`}</span></td>
                                    <td className="py-3 align-middle"><StatusBadge status={m.is_active ? 'active' : 'inactive'} /></td>
                                    <td className="py-3 pe-4 align-middle text-end">
                                        <Button variant="outline-danger" size="sm" className="d-inline-flex align-items-center gap-1 rounded-pill" onClick={() => remove(m.id)}>
                                            <Trash2 size={12} /> Remove
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </Card>
        </>
    );
};


// ═══════════════════════════════════════════════════════════════════════════ //
//  QUOTA TAB                                                                  //
// ═══════════════════════════════════════════════════════════════════════════ //

const QuotaTab = ({ slug }) => {
    const [view,           setView]          = useState('policies');
    const [defaultPolicy,  setDefaultPolicy] = useState(null);
    const [students,       setStudents]      = useState([]);
    const [analytics,      setAnalytics]     = useState(null);
    const [loading,        setLoading]       = useState(true);
    const [saving,         setSaving]        = useState(false);
    const [editingDefault, setEditingDefault]= useState(false);
    const [editStudent,    setEditStudent]   = useState(null);
    const [defaultForm,    setDefaultForm]   = useState({ daily_limit: '', weekly_limit: '', monthly_limit: '' });
    const [studentForm,    setStudentForm]   = useState({ daily_limit: '', weekly_limit: '', monthly_limit: '', notes: '' });
    const [uploadMsg,      setUploadMsg]     = useState(null);
    const [alert,          showAlert]        = useAlert();

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [defRes, stuRes] = await Promise.all([
                api.get(`/tenant/${slug}/askos/admin/quota/default`,  { headers: adminAuthHeader() }),
                api.get(`/tenant/${slug}/askos/admin/quota/students`, { headers: adminAuthHeader() }),
            ]);
            setDefaultPolicy(defRes.data);
            setDefaultForm({
                daily_limit:   defRes.data.daily_limit   != null ? String(defRes.data.daily_limit)   : '',
                weekly_limit:  defRes.data.weekly_limit  != null ? String(defRes.data.weekly_limit)  : '',
                monthly_limit: defRes.data.monthly_limit != null ? String(defRes.data.monthly_limit) : '',
            });
            setStudents(stuRes.data);
        } catch { /* empty */ }
        setLoading(false);
    }, [slug]);

    const loadAnalytics = useCallback(async () => {
        try {
            const res = await api.get(`/tenant/${slug}/askos/admin/quota/analytics?days=30`, { headers: adminAuthHeader() });
            setAnalytics(res.data);
        } catch { /* empty */ }
    }, [slug]);

    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => { if (view === 'analytics') loadAnalytics(); }, [view, loadAnalytics]);

    const saveDefault = async () => {
        setSaving(true);
        try {
            await api.put(`/tenant/${slug}/askos/admin/quota/default`, {
                daily_limit:   defaultForm.daily_limit   ? parseFloat(defaultForm.daily_limit)   : null,
                weekly_limit:  defaultForm.weekly_limit  ? parseFloat(defaultForm.weekly_limit)  : null,
                monthly_limit: defaultForm.monthly_limit ? parseFloat(defaultForm.monthly_limit) : null,
            }, { headers: adminAuthHeader() });
            showAlert('Default policy saved.');
            setEditingDefault(false);
            loadAll();
        } catch (e) { showAlert(e.response?.data?.detail || 'Save failed.', 'danger'); }
        setSaving(false);
    };

    const saveStudentPolicy = async () => {
        if (!editStudent) return;
        setSaving(true);
        try {
            await api.put(`/tenant/${slug}/askos/admin/quota/${editStudent.student_id}`, {
                daily_limit:   studentForm.daily_limit   ? parseFloat(studentForm.daily_limit)   : null,
                weekly_limit:  studentForm.weekly_limit  ? parseFloat(studentForm.weekly_limit)  : null,
                monthly_limit: studentForm.monthly_limit ? parseFloat(studentForm.monthly_limit) : null,
                notes:         studentForm.notes || null,
            }, { headers: adminAuthHeader() });
            showAlert(`Quota set for ${editStudent.email}.`);
            setEditStudent(null);
            loadAll();
        } catch (e) { showAlert(e.response?.data?.detail || 'Save failed.', 'danger'); }
        setSaving(false);
    };

    const removeStudentPolicy = async (s) => {
        try {
            await api.delete(`/tenant/${slug}/askos/admin/quota/${s.student_id}`, { headers: adminAuthHeader() });
            showAlert('Override removed — student now follows default policy.', 'warning');
            loadAll();
        } catch (e) { showAlert(e.response?.data?.detail || 'Remove failed.', 'danger'); }
    };

    const handleBulkUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploadMsg(null);
        const fd = new FormData();
        fd.append('file', file);
        try {
            const res = await api.post(`/tenant/${slug}/askos/admin/quota/bulk`, fd, { headers: adminAuthHeader() });
            setUploadMsg({ type: 'success', text: `Created: ${res.data.created}, Updated: ${res.data.updated}` });
            loadAll();
        } catch (e) { setUploadMsg({ type: 'danger', text: e.response?.data?.detail || 'Upload failed.' }); }
        e.target.value = '';
    };

    const LimitDisplay = ({ v }) =>
        v != null ? <span className="fw-semibold">{Number(v).toFixed(1)} cr</span>
                  : <span className="text-muted x-small">Unlimited</span>;

    const UsageBar = ({ used, limit, pct }) => {
        if (limit == null) return <span className="x-small text-muted">—</span>;
        const color = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#10b981';
        return (
            <div>
                <div className="d-flex justify-content-between mb-1">
                    <span className="x-small" style={{ color }}>{Number(used).toFixed(2)} / {Number(limit).toFixed(1)}</span>
                    <span className="x-small text-muted">{Math.round(pct)}%</span>
                </div>
                <div className="rounded-pill overflow-hidden" style={{ height: 4, background: '#e2e8f0' }}>
                    <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: color, borderRadius: 999 }} />
                </div>
            </div>
        );
    };

    const LimitInput = ({ label, field, form, setForm }) => (
        <Form.Group className="mb-3">
            <Form.Label className="x-small fw-bold text-muted text-uppercase">{label}</Form.Label>
            <Form.Control type="number" step="0.01" min="0" placeholder="Leave blank for unlimited"
                value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent" />
            <Form.Text className="x-small text-muted">Credits · blank = unlimited</Form.Text>
        </Form.Group>
    );

    return (
        <>
            {alert && <Alert variant={alert.variant} className="py-2 small mb-3" role="status">{alert.msg}</Alert>}

            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex gap-2">
                    <Button size="sm" variant={view === 'policies'  ? 'primary' : 'outline-secondary'}
                        className="rounded-pill d-flex align-items-center gap-1" onClick={() => setView('policies')}>
                        <Zap size={13} />Policies
                    </Button>
                    <Button size="sm" variant={view === 'analytics' ? 'primary' : 'outline-secondary'}
                        className="rounded-pill d-flex align-items-center gap-1" onClick={() => setView('analytics')}>
                        <BarChart2 size={13} />Analytics
                    </Button>
                </div>
                <label className="btn btn-sm btn-outline-secondary rounded-pill d-flex align-items-center gap-1 mb-0"
                    style={{ cursor: 'pointer' }}>
                    <Upload size={13} />Bulk CSV
                    <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleBulkUpload} />
                </label>
            </div>

            {uploadMsg && <Alert variant={uploadMsg.type} className="py-2 small mb-3" role="status">{uploadMsg.text}</Alert>}

            {view === 'policies' && (
                <>
                    <Card className="faculty-card mb-4">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <div>
                                    <p className="x-small fw-bold text-muted text-uppercase mb-0">
                                        <Zap size={12} className="me-1" />Default Policy
                                    </p>
                                    <p className="x-small text-muted mb-0">Applied to all students without an individual override.</p>
                                </div>
                                <Button size="sm" variant="outline-secondary" className="rounded-pill"
                                    onClick={() => setEditingDefault(e => !e)}>
                                    {editingDefault ? 'Cancel' : 'Edit'}
                                </Button>
                            </div>
                            {!editingDefault ? (
                                <Row className="g-3">
                                    {[
                                        { label: 'Daily Limit',   v: defaultPolicy?.daily_limit   },
                                        { label: 'Weekly Limit',  v: defaultPolicy?.weekly_limit  },
                                        { label: 'Monthly Limit', v: defaultPolicy?.monthly_limit },
                                    ].map(({ label, v }) => (
                                        <Col xs={4} key={label}>
                                            <div className="text-center p-3 rounded-3"
                                                style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                <p className="x-small text-muted text-uppercase mb-1">{label}</p>
                                                <p className="fw-bold mb-0" style={{ fontSize: '1.1rem' }}>
                                                    {v != null ? `${Number(v).toFixed(1)} cr` : '∞'}
                                                </p>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            ) : (
                                <Row className="g-3 align-items-end">
                                    <Col md={3}><LimitInput label="Daily Limit"   field="daily_limit"   form={defaultForm} setForm={setDefaultForm} /></Col>
                                    <Col md={3}><LimitInput label="Weekly Limit"  field="weekly_limit"  form={defaultForm} setForm={setDefaultForm} /></Col>
                                    <Col md={3}><LimitInput label="Monthly Limit" field="monthly_limit" form={defaultForm} setForm={setDefaultForm} /></Col>
                                    <Col md={3}>
                                        <Button variant="primary" className="w-100 rounded-pill mb-3" onClick={saveDefault} disabled={saving}>
                                            {saving ? <Spinner animation="border" size="sm" aria-hidden="true" /> : 'Save Default'}
                                        </Button>
                                    </Col>
                                </Row>
                            )}
                        </Card.Body>
                    </Card>

                    <Card className="faculty-card">
                        <Card.Header className="bg-transparent border-0 pt-3 px-4 pb-0">
                            <p className="x-small fw-bold text-muted text-uppercase mb-0">
                                Student Quotas <Badge bg="secondary" className="ms-2 fw-normal">{students.length}</Badge>
                            </p>
                        </Card.Header>
                        <Table responsive hover className="mb-0 small">
                            <thead className="table-light">
                                <tr>
                                    <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3 px-4">Student</th>
                                    <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Daily</th>
                                    <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Monthly</th>
                                    <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Today</th>
                                    <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">This Month</th>
                                    <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3 text-end pe-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={6} className="text-center py-4"><Spinner animation="border" size="sm" aria-hidden="true" /></td></tr>
                                ) : students.length === 0 ? (
                                    <EmptyRow cols={6} icon={Users} message="No students yet." />
                                ) : students.map(s => (
                                    <tr key={s.student_id}>
                                        <td className="py-3 px-4">
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center fw-bold text-primary flex-shrink-0"
                                                    style={{ width: 32, height: 32, fontSize: '0.75rem' }}>
                                                    {(s.full_name || s.email || '?')[0].toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className="fw-semibold text-dark">{s.full_name || '—'}</div>
                                                    <div className="x-small text-muted">{s.email}</div>
                                                    {s.has_override && (
                                                        <Badge className="badge-soft-primary x-small fw-normal">Override</Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-3 align-middle"><LimitDisplay v={s.daily_limit} /></td>
                                        <td className="py-3 align-middle"><LimitDisplay v={s.monthly_limit} /></td>
                                        <td className="py-3 align-middle" style={{ minWidth: 120 }}>
                                            <UsageBar used={s.daily_used}
                                                limit={s.daily_limit ?? defaultPolicy?.daily_limit}
                                                pct={s.daily_limit ? (s.daily_used / s.daily_limit * 100) : 0} />
                                        </td>
                                        <td className="py-3 align-middle" style={{ minWidth: 120 }}>
                                            <UsageBar used={s.monthly_used}
                                                limit={s.monthly_limit ?? defaultPolicy?.monthly_limit}
                                                pct={s.monthly_limit ? (s.monthly_used / s.monthly_limit * 100) : 0} />
                                        </td>
                                        <td className="py-3 pe-4 align-middle text-end">
                                            <div className="d-flex align-items-center justify-content-end gap-2">
                                                <Button size="sm" variant="outline-secondary"
                                                    className="rounded-pill d-flex align-items-center gap-1"
                                                    onClick={() => {
                                                        setEditStudent(s);
                                                        setStudentForm({
                                                            daily_limit:   s.daily_limit   != null ? String(s.daily_limit)   : '',
                                                            weekly_limit:  s.weekly_limit  != null ? String(s.weekly_limit)  : '',
                                                            monthly_limit: s.monthly_limit != null ? String(s.monthly_limit) : '',
                                                            notes: s.notes || '',
                                                        });
                                                    }}>
                                                    <Zap size={12} />Set Quota
                                                </Button>
                                                {s.has_override && (
                                                    <Button size="sm" variant="outline-danger"
                                                        className="rounded-pill d-flex align-items-center gap-1"
                                                        onClick={() => removeStudentPolicy(s)}>
                                                        <Trash2 size={12} />Reset
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card>
                </>
            )}

            {view === 'analytics' && (
                analytics ? (
                    <Row className="g-4">
                        <Col md={6}>
                            <Card className="faculty-card h-100">
                                <Card.Header className="bg-transparent border-0 pt-3 px-4 pb-0">
                                    <p className="x-small fw-bold text-muted text-uppercase mb-0">Top Consumers — 30 Days</p>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Table hover className="mb-0 small">
                                        <thead className="table-light">
                                            <tr>
                                                <th scope="col" className="py-2 px-4 x-small fw-bold text-muted text-uppercase">Student</th>
                                                <th scope="col" className="py-2 x-small fw-bold text-muted text-uppercase">Credits</th>
                                                <th scope="col" className="py-2 x-small fw-bold text-muted text-uppercase">Msgs</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {analytics.top_consumers.map((c, i) => (
                                                <tr key={c.student_id}>
                                                    <td className="py-2 px-4">
                                                        <span className="text-muted me-2 x-small">{i + 1}.</span>{c.name}
                                                    </td>
                                                    <td className="py-2 fw-semibold">{Number(c.total_credits).toFixed(2)}</td>
                                                    <td className="py-2 text-muted">{c.message_count}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="faculty-card h-100">
                                <Card.Header className="bg-transparent border-0 pt-3 px-4 pb-0">
                                    <p className="x-small fw-bold text-muted text-uppercase mb-0">By Platform</p>
                                </Card.Header>
                                <Card.Body className="px-4">
                                    {analytics.platform_breakdown.map(p => (
                                        <div key={p.platform} className="d-flex justify-content-between py-2 border-bottom">
                                            <span className="small text-capitalize">{p.platform}</span>
                                            <div className="text-end">
                                                <div className="fw-semibold small">{Number(p.credits).toFixed(2)} cr</div>
                                                <div className="x-small text-muted">{p.messages} msgs</div>
                                            </div>
                                        </div>
                                    ))}
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="faculty-card h-100">
                                <Card.Header className="bg-transparent border-0 pt-3 px-4 pb-0">
                                    <p className="x-small fw-bold text-muted text-uppercase mb-0">Costliest Topics</p>
                                </Card.Header>
                                <Card.Body className="px-4">
                                    {analytics.top_concepts.map(c => (
                                        <div key={c.concept} className="py-2 border-bottom">
                                            <div className="small fw-semibold text-truncate">{c.concept}</div>
                                            <div className="x-small text-muted">{Number(c.credits).toFixed(2)} cr · {c.questions} q</div>
                                        </div>
                                    ))}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                ) : <div className="text-center py-5"><Spinner animation="border" size="sm" aria-hidden="true" /></div>
            )}

            <Modal show={!!editStudent} onHide={() => setEditStudent(null)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-6">Set Quota — {editStudent?.full_name || editStudent?.email}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="small text-muted mb-4">Leave blank to use the default policy for that period.</p>
                    <LimitInput label="Daily Limit (credits)"   field="daily_limit"   form={studentForm} setForm={setStudentForm} />
                    <LimitInput label="Weekly Limit (credits)"  field="weekly_limit"  form={studentForm} setForm={setStudentForm} />
                    <LimitInput label="Monthly Limit (credits)" field="monthly_limit" form={studentForm} setForm={setStudentForm} />
                    <Form.Group>
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Admin Note (optional)</Form.Label>
                        <Form.Control as="textarea" rows={2} value={studentForm.notes}
                            onChange={e => setStudentForm(f => ({ ...f, notes: e.target.value }))}
                            placeholder="e.g. Research student — extended quota" className="shadow-none" />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button variant="light" onClick={() => setEditStudent(null)}>Cancel</Button>
                    <Button variant="primary" className="rounded-pill px-4" onClick={saveStudentPolicy} disabled={saving}>
                        {saving ? <Spinner animation="border" size="sm" aria-hidden="true" /> : 'Save Quota'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};


// ═══════════════════════════════════════════════════════════════════════════ //
//  CURRENT AFFAIRS TAB — RSS feed management (Phase 1)                       //
// ═══════════════════════════════════════════════════════════════════════════ //
const emptyFeedForm = { name: '', url: '', source_label: '', poll_interval_min: '60', is_active: true };

const CurrentAffairsTab = ({ slug }) => {
    const [feeds, setFeeds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [refreshingId, setRefreshingId] = useState(null);
    const [editFeed, setEditFeed] = useState(null); // null = closed, {} = create, {...feed} = edit
    const [form, setForm] = useState(emptyFeedForm);
    const [alert, showAlert] = useAlert();

    const loadFeeds = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/tenant/${slug}/askos/admin/current-affairs/feeds`, { headers: adminAuthHeader() });
            setFeeds(res.data);
        } catch { /* empty */ }
        setLoading(false);
    }, [slug]);

    useEffect(() => { loadFeeds(); }, [loadFeeds]);

    const openCreate = () => { setForm(emptyFeedForm); setEditFeed({}); };
    const openEdit = (feed) => {
        setForm({
            name: feed.name,
            url: feed.url,
            source_label: feed.source_label,
            poll_interval_min: String(feed.poll_interval_min),
            is_active: feed.is_active,
        });
        setEditFeed(feed);
    };

    const saveFeed = async () => {
        setSaving(true);
        const payload = {
            name: form.name,
            url: form.url,
            source_label: form.source_label,
            poll_interval_min: parseInt(form.poll_interval_min, 10) || 60,
            is_active: form.is_active,
        };
        try {
            if (editFeed && editFeed.id) {
                await api.patch(`/tenant/${slug}/askos/admin/current-affairs/feeds/${editFeed.id}`, payload, { headers: adminAuthHeader() });
                showAlert('Feed updated.');
            } else {
                await api.post(`/tenant/${slug}/askos/admin/current-affairs/feeds`, payload, { headers: adminAuthHeader() });
                showAlert('Feed created.');
            }
            setEditFeed(null);
            loadFeeds();
        } catch (e) {
            showAlert(e.response?.data?.detail || 'Save failed.', 'danger');
        }
        setSaving(false);
    };

    const deleteFeed = async (feed) => {
        try {
            await api.delete(`/tenant/${slug}/askos/admin/current-affairs/feeds/${feed.id}`, { headers: adminAuthHeader() });
            showAlert('Feed deleted.', 'warning');
            loadFeeds();
        } catch (e) {
            showAlert(e.response?.data?.detail || 'Delete failed.', 'danger');
        }
    };

    const refreshFeed = async (feed) => {
        setRefreshingId(feed.id);
        try {
            const res = await api.post(`/tenant/${slug}/askos/admin/current-affairs/feeds/${feed.id}/refresh`, {}, { headers: adminAuthHeader() });
            if (res.data.error) {
                showAlert(`Refresh failed: ${res.data.error}`, 'danger');
            } else {
                showAlert(`Inserted ${res.data.inserted}, updated ${res.data.updated}.`);
            }
            loadFeeds();
        } catch (e) {
            showAlert(e.response?.data?.detail || 'Refresh failed.', 'danger');
        }
        setRefreshingId(null);
    };

    if (loading) return <div className="text-center py-5"><Spinner animation="border" /></div>;

    return (
        <>
            {alert && <Alert variant={alert.variant} className="py-2 small">{alert.msg}</Alert>}

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0">Current Affairs Feeds</h6>
                <Button size="sm" className="rounded-pill px-3" onClick={openCreate}>
                    <Plus size={14} className="me-1" /> Add Feed
                </Button>
            </div>

            <Card className="border-0 shadow-sm">
                <Table hover responsive className="mb-0 align-middle">
                    <thead className="table-light">
                        <tr>
                            <th>Name</th>
                            <th>Source</th>
                            <th>Interval</th>
                            <th>Status</th>
                            <th>Last Polled</th>
                            <th className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {feeds.map(f => (
                            <tr key={f.id}>
                                <td>
                                    <div className="fw-semibold x-small">{f.name}</div>
                                    <div className="text-muted x-small text-truncate" style={{ maxWidth: 280 }}>{f.url}</div>
                                </td>
                                <td><Badge bg="light" text="dark" className="border">{f.source_label}</Badge></td>
                                <td className="x-small">{f.poll_interval_min} min</td>
                                <td>
                                    {!f.is_active
                                        ? <Badge bg="secondary" bg-opacity="10">disabled</Badge>
                                        : f.last_status === 'error'
                                            ? <Badge bg="danger" title={f.last_error}>error</Badge>
                                            : <Badge bg="success">ok</Badge>}
                                </td>
                                <td className="x-small text-muted">{f.last_polled_at ? new Date(f.last_polled_at).toLocaleString() : 'never'}</td>
                                <td className="text-end">
                                    <Button variant="link" size="sm" className="p-1" title="Refresh now"
                                        onClick={() => refreshFeed(f)} disabled={refreshingId === f.id}>
                                        {refreshingId === f.id ? <Spinner animation="border" size="sm" /> : <ExternalLink size={14} />}
                                    </Button>
                                    <Button variant="link" size="sm" className="p-1" title="Edit" onClick={() => openEdit(f)}>
                                        <Pencil size={14} />
                                    </Button>
                                    <Button variant="link" size="sm" className="p-1 text-danger" title="Delete" onClick={() => deleteFeed(f)}>
                                        <Trash2 size={14} />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {feeds.length === 0 && (
                            <tr><td colSpan={6} className="text-center text-muted py-4">No feeds configured yet.</td></tr>
                        )}
                    </tbody>
                </Table>
            </Card>

            <Modal show={!!editFeed} onHide={() => setEditFeed(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fs-6 fw-bold">{editFeed?.id ? 'Edit Feed' : 'Add Feed'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Name</Form.Label>
                        <Form.Control value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. PIB — Releases (ModId 6)" />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">RSS URL</Form.Label>
                        <Form.Control value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://…" />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Source Label</Form.Label>
                        <Form.Control value={form.source_label} onChange={e => setForm(f => ({ ...f, source_label: e.target.value }))} placeholder="e.g. PIB" />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Poll Interval (minutes)</Form.Label>
                        <Form.Control type="number" value={form.poll_interval_min} onChange={e => setForm(f => ({ ...f, poll_interval_min: e.target.value }))} />
                    </Form.Group>
                    <Form.Check type="checkbox" label="Active" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button variant="light" onClick={() => setEditFeed(null)}>Cancel</Button>
                    <Button variant="primary" className="rounded-pill px-4" onClick={saveFeed} disabled={saving || !form.name || !form.url || !form.source_label}>
                        {saving ? <Spinner animation="border" size="sm" aria-hidden="true" /> : 'Save Feed'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};


// ═══════════════════════════════════════════════════════════════════════════ //
//  MAIN ADMIN APP                                                             //
// ═══════════════════════════════════════════════════════════════════════════ //
const AskOSAdminApp = ({ slug, onLogout }) => {
    const [activeTab, setActiveTab] = useState('students');
    const [branding, setBranding]   = useState(null); // 🌟 NEW: Branding state
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // 🌟 NEW: Fetch branding for the Admin Navbar
    useEffect(() => {
        if (slug) {
            api.get(`/public/tenant-branding/${slug}`)
               .then(res => setBranding({ name: res.data.name, logo: res.data.logo }))
               .catch(() => {});
        }
    }, [slug]);

    const handleLogout = () => {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_slug');
        dispatch(logout());
        if (onLogout) onLogout(); 
        navigate('/askos-admin');
    };

    const TABS = [
        { key: 'students', label: 'Students',       icon: Users   },
        { key: 'access',   label: 'Project Access', icon: BookOpen },
        { key: 'mappings', label: 'Course Mapping', icon: Link2   },
        { key: 'quota',    label: 'Credit Quotas', icon: Zap    },
        { key: 'current-affairs', label: 'Current Affairs', icon: Newspaper },
    ];

    return (
        <div className="classroom-body min-vh-100">
            <nav className="bg-white border-bottom px-4 py-2 d-flex align-items-center justify-content-between"
                style={{ boxShadow: 'var(--classroom-card-shadow)' }}>
                
                <div className="d-flex align-items-center gap-3">
                    {/* 🌟 NEW: Branding Logo with Local Fallback */}
                    {branding?.logo ? (
                        <img 
                            src={branding.logo} 
                            alt="Logo" 
                            height="32" 
                            style={{ objectFit: 'contain', maxWidth: '140px' }}
                            onError={(e) => { 
                                e.target.onerror = null; 
                                e.target.src = "/assets/images/logo.png"; // Failsafe fallback
                            }} 
                        />
                    ) : (
                        <div className="bg-primary rounded-2 d-flex align-items-center justify-content-center" style={{ width: 32, height: 32 }}>
                            <GraduationCap size={18} className="text-white" />
                        </div>
                    )}
                    <div>
                        <span className="fw-bold text-dark">{branding?.name ? `${branding.name} Admin` : 'AskOS Admin'}</span>
                        <span className="text-muted ms-2 small">· {slug.toUpperCase()}</span>
                    </div>
                </div>

                <Button variant="link" size="sm" className="text-muted p-0 d-flex align-items-center gap-1" onClick={handleLogout}>
                    <LogOut size={15} />
                    <span className="x-small">Sign out</span>
                </Button>
            </nav>

            <Container fluid className="py-4 px-4">
                <Nav variant="pills" className="mb-4 gap-1"
                    style={{ background: '#f1f3f5', padding: '4px', borderRadius: 12, display: 'inline-flex' }}>
                    {TABS.map(t => (
                        <Nav.Item key={t.key}>
                            <Nav.Link active={activeTab === t.key} onClick={() => setActiveTab(t.key)}
                                className="d-flex align-items-center gap-2 px-3 py-2"
                                style={{ borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem' }}>
                                <t.icon size={14} />{t.label}
                            </Nav.Link>
                        </Nav.Item>
                    ))}
                </Nav>

                {activeTab === 'students' && <StudentsTab slug={slug} />}
                {activeTab === 'access'   && <ProjectAccessTab slug={slug} />}
                {activeTab === 'mappings' && <CourseMappingTab slug={slug} />}
                {activeTab === 'quota'    && <QuotaTab    slug={slug} />}
                {activeTab === 'current-affairs' && <CurrentAffairsTab slug={slug} />}
            </Container>
        </div>
    );
};


// ═══════════════════════════════════════════════════════════════════════════ //
//  ROOT — auth gate                                                           //
// ═══════════════════════════════════════════════════════════════════════════ //
const AskOSAdmin = () => {
    const storedToken = localStorage.getItem('admin_token');
    const storedSlug  = localStorage.getItem('admin_slug');
    const [authed, setAuthed] = useState(!!storedToken);
    const [slug, setSlug]     = useState(storedSlug || '');

    if (!authed) {
        return <AdminLogin onLogin={(s) => { setSlug(s); setAuthed(true); }} />;
    }
    return <AskOSAdminApp slug={slug} onLogout={() => setAuthed(false)} />;
};

export default AskOSAdmin;